/**
 * <dice-addon>
 *
 * Optional "add a dice set" picker for a product page. The dice set is a
 * separate product (with its own stock per colour), so the main product keeps
 * selling even when a dice colour runs out.
 *
 * - "No dice" is selected by default. If the block setting "preselect_matching"
 *   is on, instead pre-selects the dice colour that matches the product's colour
 *   (via the shared VariantGatedElement base class), until the customer makes
 *   their own choice.
 * - Exposes getCartAddOnItems(), which assets/product-form.js calls on submit
 *   to add the chosen dice set to the cart alongside the main product.
 *
 * Requires variant-gated-element.js to be loaded first.
 */
class DiceAddon extends VariantGatedElement {
  constructor() {
    super();
    this.formId = this.dataset.formId;
    this.pairedPropertyName = this.dataset.pairedPropertyName || 'For';
    this.pairedProductTitle = this.dataset.pairedProductTitle || '';
    this.inputs = Array.from(this.querySelectorAll('[data-dice-input]'));
    this.customerChose = false;
    this.preselectMatching = this.dataset.preselectMatching === 'true';

    this.inputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.customerChose = true;
      });
    });

    this.initVariantGate();
    this.initHideOption();
  }

  // Optional: hide the picker while another option (e.g. Size) starts with a
  // given value (e.g. "Large"). Hiding resets to "No dice" so none are added.
  initHideOption() {
    this.hideOptionPosition = parseInt(this.dataset.hideOptionPosition, 10) || 0;
    this.hideOptionValue = this.dataset.hideOptionValue || '';
    if (!this.hideOptionPosition || !this.hideOptionValue || typeof subscribe !== 'function') return;

    this._unsubscribeHideOption = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data.sectionId !== this.sectionId) return;
      const variant = event.data.variant;
      const value = variant ? variant[`option${this.hideOptionPosition}`] || '' : '';
      this.setHidden(value.startsWith(this.hideOptionValue));
    });
  }

  setHidden(hidden) {
    this.hidden = hidden;
    if (!hidden) return;
    const none = this.inputs.find((input) => input.value === '');
    if (none) none.checked = true;
    this.customerChose = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._unsubscribeHideOption) this._unsubscribeHideOption();
  }

  // Called with the product's currently selected colour.
  onGateValueChange(colour) {
    if (!this.preselectMatching || this.customerChose || !colour) return;
    const match = this.inputs.find((input) => input.dataset.diceColour === colour && !input.disabled);
    const fallback = this.inputs.find((input) => input.value === '');
    const target = match || fallback;
    if (target) target.checked = true;
  }

  get selectedVariantId() {
    const checked = this.inputs.find((input) => input.checked);
    return checked ? checked.value : '';
  }

  get quantity() {
    const input =
      document.querySelector(`[name="quantity"][form="${this.formId}"]`) ||
      document.querySelector(`#${this.formId} [name="quantity"]`);
    const value = input ? parseInt(input.value, 10) : 1;
    return value > 0 ? value : 1;
  }

  getCartAddOnItems() {
    if (this.hidden) return [];
    const id = this.selectedVariantId;
    if (!id) return [];
    const properties = {};
    if (this.pairedProductTitle) properties[this.pairedPropertyName] = this.pairedProductTitle;
    return [{ id: parseInt(id, 10), quantity: this.quantity, properties }];
  }
}

if (!customElements.get('dice-addon')) customElements.define('dice-addon', DiceAddon);
